import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Button, Grid, Stack, Typography } from "@mui/material";
import { Link, useParams } from "react-router-dom";

import {
  listCoreNetworkMemberships,
  listCoreNetworkOffers,
  listCoreNetworkWorkRequests,
  getCoreNetworkArtworkWorkloadReport,
} from "../../../services/coreNetwork";
import {
  CoreNetworkShell,
  CORE_NETWORK_LABEL,
  ErrorState,
  LoadingState,
  MetricCard,
  PagedTable,
  SectionCard,
  StatusChip,
  buildCoreNetworkPath,
  computeDashboardStats,
  formatDateTime,
  safeArray,
  toTitleCase,
} from "./coreNetworkUiShared";
import CoreNetworkArtworkReporting from "./components/CoreNetworkArtworkReporting";
import { AuthContext } from "../../../helpers/AuthContext";

export default function CoreNetworkDashboard() {
  const { instanceUniqueId } = useParams();
  const { authState } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [workRequests, setWorkRequests] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [offersByRequest, setOffersByRequest] = useState({});
  const [artworkSource, setArtworkSource] = useState("all");
  const [artworkReport, setArtworkReport] = useState(null);
  const [reportingError, setReportingError] = useState("");
  const viewerRoles = Array.from(
    new Set(
      []
        .concat(authState?.roles || [])
        .concat(authState?.role ? [authState.role] : [])
        .concat(authState?.user?.roles || [])
        .concat(authState?.user?.role ? [authState.user.role] : [])
        .map((role) => String(role || "").trim().toLowerCase().replace(/_/g, "-"))
        .filter(Boolean)
    )
  );
  const isRelatedScopeDashboard =
    viewerRoles.includes("client-rep") || viewerRoles.includes("order-rep");

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setReportingError("");
      const [workResponse, membershipResponse, reportingResponse] = await Promise.all([
        listCoreNetworkWorkRequests({ instanceUniqueId }),
        isRelatedScopeDashboard ? Promise.resolve(null) : listCoreNetworkMemberships({ instanceUniqueId }),
        isRelatedScopeDashboard
          ? Promise.resolve(null)
          : getCoreNetworkArtworkWorkloadReport({ instanceUniqueId, source: artworkSource }).catch((reportError) => ({
              reportingError: reportError?.response?.data?.message || reportError.message || "Artwork reporting is temporarily unavailable.",
            })),
      ]);
      const nextWorkRequests = safeArray(workResponse.workRequests);
      setWorkRequests(nextWorkRequests);
      setMemberships(safeArray(membershipResponse?.memberships));
      if (!reportingResponse) {
        setArtworkReport(null);
      } else if (reportingResponse.reportingError) {
        setArtworkReport(null);
        setReportingError(reportingResponse.reportingError);
      } else {
        setArtworkReport(reportingResponse);
      }

      const openRequests = nextWorkRequests.filter((request) =>
        ["submitted", "matched", "offered", "assigned", "accepted", "in_progress", "awaiting_review", "revision_requested"].includes(
          String(request.status || "").toLowerCase()
        )
      );

      if (isRelatedScopeDashboard) {
        setOffersByRequest({});
      } else {
        const offersEntries = await Promise.all(
          openRequests.slice(0, 20).map(async (request) => {
            try {
              const offerResponse = await listCoreNetworkOffers(request.id, { instanceUniqueId });
              return [request.id, safeArray(offerResponse.offers)];
            } catch (_error) {
              return [request.id, []];
            }
          })
        );
        setOffersByRequest(Object.fromEntries(offersEntries));
      }
    } catch (loadError) {
      setError(
        loadError?.response?.data?.message ||
          loadError.message ||
          "Unable to load Core Network dashboard."
      );
    } finally {
      setLoading(false);
    }
  }, [artworkSource, instanceUniqueId, isRelatedScopeDashboard]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const stats = useMemo(
    () => computeDashboardStats(workRequests, memberships, offersByRequest),
    [memberships, offersByRequest, workRequests]
  );

  const recentRequests = useMemo(
    () => [...workRequests].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)),
    [workRequests]
  );

  return (
    <CoreNetworkShell
      title="Core Network Dashboard"
      breadcrumb={`${CORE_NETWORK_LABEL} / Dashboard`}
    >
      {loading ? <LoadingState label="Loading Core Network dashboard..." /> : null}
      {!loading && error ? <ErrorState message={error} onAction={loadDashboard} /> : null}
      {!loading && !error ? (
        <Stack spacing={3}>
          {reportingError ? <Alert severity="warning">{reportingError} Core Network operational metrics remain available below.</Alert> : null}
          {isRelatedScopeDashboard ? null : (
            <CoreNetworkArtworkReporting
              report={artworkReport}
              source={artworkSource}
              onSourceChange={setArtworkSource}
              instanceUniqueId={instanceUniqueId}
            />
          )}

          <SectionCard
            title="Core Network operations"
            subtitle={isRelatedScopeDashboard ? "Your assigned Core Network work requests only." : "Assignment, offer, provider, and review metrics remain Core Network-only. Binated is never included in these operational counts."}
          >
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={3}><MetricCard label="Open requests" value={stats.openRequests} /></Grid>
            <Grid item xs={12} md={3}><MetricCard label="Matched" value={stats.matched} /></Grid>
            <Grid item xs={12} md={3}><MetricCard label="Offers sent" value={stats.offersSent} /></Grid>
            <Grid item xs={12} md={3}><MetricCard label="Assigned / in progress" value={stats.assignedInProgress} /></Grid>
            <Grid item xs={12} md={3}><MetricCard label="Awaiting review" value={stats.awaitingReview} /></Grid>
            <Grid item xs={12} md={3}><MetricCard label="Revision requested" value={stats.revisionRequested} /></Grid>
            <Grid item xs={12} md={3}><MetricCard label="Completed this week" value={stats.completedThisWeek} /></Grid>
            <Grid item xs={12} md={3}><MetricCard label="Overdue" value={stats.overdue} helperText="Past deadline and still active" /></Grid>
            {isRelatedScopeDashboard ? null : <Grid item xs={12} md={6}><MetricCard label="Active network providers" value={stats.activeProviders} /></Grid>}
            {isRelatedScopeDashboard ? null : <Grid item xs={12} md={6}><MetricCard label="Pending offers" value={stats.pendingOffers} /></Grid>}
          </Grid>
          </SectionCard>

          <SectionCard
            title="Recently updated work"
            subtitle="A quick operational queue for active requests."
            action={<Button variant="outlined" onClick={loadDashboard}>Refresh</Button>}
          >
            <PagedTable
              rows={recentRequests}
              columns={[
                {
                  key: "title",
                  label: "Request",
                  render: (row) => (
                    <Stack spacing={0.5}>
                      <Typography
                        component={Link}
                        to={buildCoreNetworkPath(instanceUniqueId, `work-requests/${row.id}`)}
                        sx={{ fontWeight: 700, textDecoration: "none", color: "#103a71" }}
                      >
                        {row.title || `Request #${row.id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {toTitleCase(row.requestType)} | {toTitleCase(row.fulfillmentProvider)}
                      </Typography>
                    </Stack>
                  ),
                },
                { key: "status", label: "Status", render: (row) => <StatusChip value={row.status} /> },
                { key: "networkType", label: "Network", render: (row) => toTitleCase(row.networkType) },
                { key: "deadlineAt", label: "Deadline", render: (row) => formatDateTime(row.deadlineAt) },
                { key: "lastActivityAt", label: "Last activity", render: (row) => formatDateTime(row.lastActivityAt) },
              ]}
              emptyTitle="No requests yet"
              emptyDescription="Create the first Core Network request from the main request queue."
            />
          </SectionCard>
        </Stack>
      ) : null}
    </CoreNetworkShell>
  );
}
